import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'politica' | 'condiciones' | null;
const politicaContent = {
  title: 'Política de Privacidad',
  body: `Última actualización: marzo 2026

1. Información que recopilamos
Recopilamos únicamente la información necesaria para procesar su compra: nombre, correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito directamente; los pagos son procesados de forma segura por nuestro proveedor de pagos.

2. Uso de la información
Su información personal se utiliza exclusivamente para:
• Procesar y entregar su compra (envío del enlace de descarga).
• Comunicarnos con usted en caso de problemas técnicos con su pedido.
• Enviar actualizaciones relacionadas con su producto, solo si usted lo autoriza.

3. Protección de datos
Implementamos medidas de seguridad razonables para proteger su información personal contra acceso no autorizado, alteración o destrucción.

4. Compartir información
No vendemos, intercambiamos ni transferimos su información personal a terceros, excepto cuando sea necesario para procesar su pedido o cuando la ley lo requiera.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Usted puede configurar su navegador para rechazar cookies.

6. Contacto
Si tiene preguntas sobre esta política, puede contactarnos a través de nuestro correo de soporte.`
};
const condicionesContent = {
  title: 'Términos y Condiciones',
  body: `Última actualización: marzo 2026

1. Producto digital
"14 Días de Liberación" es un producto digital (e-book en formato PDF) que se entrega mediante descarga inmediata después del pago.

2. Uso personal
La compra otorga una licencia de uso personal e intransferible. Queda prohibido redistribuir, revender, compartir o reproducir el contenido total o parcial del e-book sin autorización expresa.

3. Política de reembolsos
Por tratarse de un producto digital descargable, todas las ventas son finales. No se ofrecen reembolsos una vez completada la descarga. Si experimenta problemas técnicos con la descarga, contáctenos y le asistiremos.

4. Descargo de responsabilidad médica
Este material es exclusivamente educativo e informativo. No sustituye el consejo, diagnóstico o tratamiento médico profesional. Si usted tiene condiciones de salud preexistentes o toma medicación, consulte con su profesional de salud antes de realizar cambios en su alimentación, ejercicio o rutinas.

5. Propiedad intelectual
Todo el contenido del e-book, incluyendo textos, diseños, gráficos y estructura, está protegido por derechos de autor. Queda prohibida su reproducción sin autorización.

6. Modificaciones
Nos reservamos el derecho de actualizar estos términos en cualquier momento. La versión vigente estará siempre disponible en nuestro sitio.

7. Contacto
Para consultas sobre estos términos, puede escribirnos a nuestro correo de soporte.`
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-bark-900 py-8 px-6 md:px-12 w-full" data-id="element-52">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-53">
          <p className="text-sage-200/60 text-sm font-lato" data-id="element-54">
            © {new Date().getFullYear()} 14 Días de Liberación. Todos los
            derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-55">
            <button onClick={() => setActiveModal('politica')} className="text-sage-200/60 hover:text-sage-200 text-sm font-lato transition-colors duration-200 underline underline-offset-2" data-id="element-56">
              Política de Privacidad
            </button>
            <span className="text-sage-200/30" data-id="element-57">·</span>
            <button onClick={() => setActiveModal('condiciones')} className="text-sage-200/60 hover:text-sage-200 text-sm font-lato transition-colors duration-200 underline underline-offset-2" data-id="element-58">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-59">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-bark-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-60">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-cream-50 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-61">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-8 py-5 border-b border-sage-200" data-id="element-62">
                <h3 className="text-2xl font-playfair text-bark-900" data-id="element-63">
                  {activeModal === 'politica' ? politicaContent.title : condicionesContent.title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-9 h-9 rounded-full bg-sage-50 hover:bg-sage-100 flex items-center justify-center transition-colors duration-200" aria-label="Cerrar" data-id="element-64">
                  <XIcon className="w-5 h-5 text-bark-700" data-id="element-65" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6 overflow-y-auto" data-id="element-66">
                <div className="text-bark-700 font-lato text-sm leading-relaxed whitespace-pre-line" data-id="element-67">
                  {activeModal === 'politica' ? politicaContent.body : condicionesContent.body}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-8 py-4 border-t border-sage-200 flex justify-end" data-id="element-68">
                <button onClick={() => setActiveModal(null)} className="bg-sage-600 hover:bg-sage-500 text-white font-lato font-bold text-sm px-6 py-2.5 rounded-md transition-colors duration-200" data-id="element-69">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}